/* eslint-disable react-native/no-raw-text */
/**
 * StyleConsistencyInsight — json-render web wrapper
 *
 * Web implementation for the json-render catalog (web-only).
 * Communication style analysis with formality/vocabulary metrics.
 * Maps to: analysis-results/StyleConsistencySection.tsx
 *
 * Task: NocoDB #2084
 * Agent: Diego <diego@liftping.net>
 */
"use client";

import React from "react";
import { useTranslation } from "@dramadetector/shared";
import type { StyleConsistencyInsightProps } from "../schema";

export function StyleConsistencyInsight({
	formalityShift,
	vocabularyDiversity,
	punctuationPattern,
	messageLengthCategory,
	flags = [],
}: StyleConsistencyInsightProps) {
	const { t } = useTranslation("analysis") as {
		t: (key: string, options?: any) => string;
	};

	return (
		<div className="backdrop-blur-sm bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
			<h4 className="text-sm font-semibold text-[#F3F4F6]">
				{t("style_consistency.header")}
			</h4>
			<div className="flex flex-wrap gap-2">
				<span className="text-xs px-2 py-1 rounded-full bg-white/10 text-[#D1D5DB]">
					{t(`style_consistency.punctuation_pill.${punctuationPattern}`)}
				</span>
				<span className="text-xs px-2 py-1 rounded-full bg-white/10 text-[#D1D5DB]">
					{t(`style_consistency.length_pill.${messageLengthCategory}`)}
				</span>
			</div>
			<div className="space-y-2">
				<MetricBar
					label={t("style_consistency.formality_level")}
					value={formalityShift}
				/>
				<MetricBar
					label={t("style_consistency.vocabulary_diversity")}
					value={vocabularyDiversity}
				/>
			</div>
			{flags.length > 0 && (
				<div className="flex flex-wrap gap-1">
					{flags.map((flag) => (
						<span
							key={flag}
							className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-300"
						>
							{flag}
						</span>
					))}
				</div>
			)}
		</div>
	);
}

function MetricBar({ label, value }: { label: string; value: number }) {
	return (
		<div className="space-y-1">
			<div className="flex justify-between text-xs text-[#9CA3AF]">
				<span>{label}</span>
				<span>{Math.round(value * 100)}%</span>
			</div>
			<div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
				<div
					className="h-full rounded-full bg-[#8b5cf6]"
					style={{ width: `${value * 100}%` }}
				/>
			</div>
		</div>
	);
}
